/**
 * `requirePermission` factory — permission-gated route loader on top of
 * `requireAuth`. Phase 08.1 Plan 02 — replaces `requireRole({ anyOf: [...] })`
 * at the gate level; `requireRole` is retained only for chip/badge display
 * logic (CONTEXT D-07).
 *
 * Two modes (mutually exclusive — pass exactly one):
 *   - `anyOf`: passes when `snap.effectivePermissions` contains ANY of these codes.
 *   - `allOf`: passes only when EVERY code in `allOf` is present.
 *
 * Behavior contracts:
 *   - Missing auth ⇒ redirect to `/login`. Unauth is a different failure mode
 *     than insufficient privilege; the login flow owns post-login restoration.
 *   - Insufficient privilege (a DEFINED, NON-EMPTY set lacking the required
 *     code) ⇒ redirect to caller-supplied `defaultRoute`.
 *   - Authenticated but `effectivePermissions === undefined` — perms NOT YET
 *     HYDRATED (e.g. a hard reload of a gated route where the silent-refresh
 *     bootstrap mounts the router before the perms fetch resolves, or a
 *     cross-tab follower that hit the fresh-token short-circuit) ⇒ allow the
 *     route through (NO redirect). Redirecting here races the index→landing
 *     redirect and infinite-loops the router; the backend still enforces authz
 *     on every data call and the next navigation re-gates once perms hydrate.
 *     RESEARCH §Pitfall 2.
 *
 *     UNHYDRATED vs HYDRATED-EMPTY (the security fix): the snapshot type already
 *     distinguishes these — `undefined` is "fetch pending / failed, unknown"
 *     while a `Set` (even empty) is "the server answered". `200 []` is the
 *     documented, VALID response for a newly-provisioned user with zero grants
 *     (see `effectivePermissions.ts` + `fetchEffectivePermissions`). A prior fix
 *     collapsed hydrated-empty into unhydrated (`perms.size === 0 ⇒ allow`),
 *     which let a genuine zero-grant user through EVERY gate in production. That
 *     is wrong: a hydrated-empty set is a real "no access assigned" state and
 *     must DENY.
 *
 *     LOOP-BREAKER (why hydrated-empty does NOT redirect to `defaultRoute`):
 *     HR-Admin gates `/dashboard` (fallback `/me/overview`) and the `/me` parent
 *     (fallback `/dashboard`); with an empty set BOTH deny and their fallbacks
 *     point at each other → an infinite `/dashboard` ⇄ `/me/overview` redirect
 *     storm. So a hydrated-empty set redirects instead to `noAccessRoute` — an
 *     UNGATED "no access assigned" screen that can never re-trigger a gate, so
 *     no loop is possible. `fetchEffectivePermissions` fail-closes to `[]` on a
 *     401 / network error, which also lands a user on the no-access screen; that
 *     is the correct fail-closed posture (deny, don't silently grant), and a
 *     retry/relogin re-hydrates a populated set.
 *
 * Pitfall avoidance (RESEARCH §Pitfall 1): NEVER pass `defaultRoute: "/"` in
 * production call sites. HR Console routes use `/dashboard`; `/me/*` uses
 * `/me/overview`; `/team/*` uses `/me/overview`. The `/` default is a
 * pragmatic fallback for tests and is NOT safe for prod use because `/`
 * itself may be permission-gated in some app shells (loop hazard).
 *
 * Usage:
 *
 *   export const Route = createFileRoute("/_auth/payroll")({
 *     beforeLoad: requirePermission({
 *       anyOf: ["payroll:read"],
 *       defaultRoute: "/dashboard",
 *     }),
 *     component: PayrollPage,
 *   });
 */
import { redirect } from "@tanstack/react-router";
import type { RouterContext } from "./routerContext.js";

export interface RequirePermissionOpts {
  /** Any one of these permission codes is sufficient. Mutually exclusive with `allOf`. */
  anyOf?: readonly string[];
  /** Every permission in this list must be present. Mutually exclusive with `anyOf`. */
  allOf?: readonly string[];
  /**
   * Where to send users who authenticated but lack the permission.
   * Per RESEARCH §Pitfall 1, set persona-aware:
   *   - HR Console routes  → "/dashboard"
   *   - /me/* routes       → "/me/overview"
   *   - /team/* routes     → "/me/overview"
   * NEVER "/" in production (loop hazard).
   * @default "/"
   */
  defaultRoute?: string;
  /**
   * Where to send an authenticated user whose permissions HYDRATED EMPTY
   * (`200 []` — a newly-provisioned user with zero grants, or a fail-closed
   * fetch). Must be an UNGATED route so it can never re-trigger a permission
   * gate and loop; both apps ship `/no-access` for exactly this. Kept
   * overridable only for tests.
   * @default "/no-access"
   */
  noAccessRoute?: string;
}

export function requirePermission(opts: RequirePermissionOpts) {
  const fallback = opts.defaultRoute ?? "/";
  const noAccess = opts.noAccessRoute ?? "/no-access";
  return ({ context }: { context: RouterContext }): void => {
    const snap = context.auth.getSnapshot();
    if (!snap.isAuthenticated) {
      throw redirect({ to: "/login" });
    }
    const perms = snap.effectivePermissions;
    // `undefined` ⇒ perms NOT yet hydrated (hard reload races the bootstrap, or
    // a cross-tab follower is waiting on the leader's rotation). Allow through:
    // redirecting here races the index→landing redirect and loops the router,
    // and the backend re-enforces authz on every data call. The next navigation
    // re-gates once perms hydrate. RESEARCH §Pitfall 2.
    if (perms === undefined) return;
    // A HYDRATED set (even empty) means the server answered. An empty set is a
    // genuine zero-grant / fail-closed state — a real "no access" denial, NOT
    // "didn't hydrate". Send it to the UNGATED `/no-access` screen: routing it
    // to `defaultRoute` would loop (HR-Admin's `/dashboard` ⇄ `/me/overview`
    // gates point at each other), but `/no-access` has no permission gate so no
    // loop is possible. This is the security fix — a prior change let empty sets
    // pass EVERY gate.
    if (perms.size === 0) throw redirect({ to: noAccess });
    if (opts.anyOf !== undefined) {
      const ok = opts.anyOf.some((p) => perms.has(p));
      if (!ok) throw redirect({ to: fallback });
    }
    if (opts.allOf !== undefined) {
      const ok = opts.allOf.every((p) => perms.has(p));
      if (!ok) throw redirect({ to: fallback });
    }
  };
}
